import Cookies from 'js-cookie'
import { getEnv } from '@unni/platform'

import type { NextRequest, NextResponse } from 'next/server'

/**
 * 인증 토큰 기반 사용자 추적 유틸리티
 *
 * 목적:
 * - 토큰을 해시하여 익명 사용자도 일관되게 추적
 * - 동일 토큰 = 동일 사용자로 인식 (Amplitude 분석용)
 * - 토큰 갱신 시에도 사용자 연속성 유지
 * - 개인정보 보호: 토큰에서 역추적 불가능한 단방향 해시 사용
 */
export class AuthTokenTracker {
  private static readonly COOKIE_NAME = 'hashedToken'
  private static readonly HASH_LENGTH = 22 // Base64URL에서 132비트 엔트로피

  /**
   * 토큰을 SHA-256으로 해시하여 단방향 변환
   * 동일 토큰에 대해 항상 동일한 해시값 반환 (결정적)
   */
  static async generateHashedToken(token: string): Promise<string> {
    if (!token || token.trim().length === 0) {
      return ''
    }

    try {
      // Edge Runtime에서 Web Crypto API 사용
      const encoder = new TextEncoder()
      const data = encoder.encode(token.trim())
      const hashBuffer = await crypto.subtle.digest('SHA-256', data)

      // ArrayBuffer를 Base64URL로 변환
      const hashArray = new Uint8Array(hashBuffer)
      const base64 = btoa(String.fromCharCode(...hashArray))
      const base64url = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')

      return base64url.substring(0, this.HASH_LENGTH)
    } catch (error) {
      console.error('[AuthTokenTracker] Failed to generate hashed token:', error)
      return ''
    }
  }

  /**
   * 쿠키에서 토큰을 읽고 해시 생성 (서버사이드)
   */
  static async getHashedTokenFromCookie(req?: NextRequest): Promise<string | null> {
    const token = req?.cookies?.get('token')?.value
    if (!token) {
      return null
    }

    return await this.generateHashedToken(token)
  }

  /**
   * 클라이언트에서 해시된 토큰 읽기
   */
  static getClientHashedToken(): string | null {
    if (typeof window === 'undefined') {
      return null
    }

    return Cookies.get(this.COOKIE_NAME) || null
  }

  /**
   * Amplitude에 hashedToken User Property 설정
   * 초기화 보장을 위해 amplitude 인스턴스 직접 전달
   */
  static setAmplitudeHashedToken(amplitude?: any): void {
    if (typeof window === 'undefined') {
      return
    }

    const hashedToken = this.getClientHashedToken()
    if (hashedToken && amplitude) {
      try {
        const identifyEvent = new amplitude.Identify()
        identifyEvent.set('hashed_token', hashedToken)
        amplitude.identify(identifyEvent)
      } catch (error) {
        console.warn('[AuthTokenTracker] Failed to set Amplitude user property:', error)
      }
    }
  }

  /**
   * 현재 토큰 쿠키에서 직접 해시 생성 (클라이언트용)
   */
  static getCurrentHashedToken(): string | null {
    if (typeof window === 'undefined') {
      return null
    }

    // 클라이언트에서는 crypto 사용 불가하므로 저장된 해시 쿠키 사용
    return this.getClientHashedToken()
  }

  /**
   * Amplitude에서 hashedToken User Property 제거 (로그아웃용)
   */
  static clearAmplitudeHashedToken(amplitude?: any): void {
    if (typeof window === 'undefined') {
      return
    }

    if (amplitude) {
      try {
        const identifyEvent = new amplitude.Identify()
        identifyEvent.unset('hashed_token')
        amplitude.identify(identifyEvent)
        console.log('[AuthTokenTracker] hashedToken User Property removed from Amplitude')
      } catch (error) {
        console.warn('[AuthTokenTracker] Failed to remove hashedToken from Amplitude:', error)
      }
    }
  }
}
